// @ts-expect-error TS(7016): Could not find a declaration file for module 'hype... Remove this comment to see the full error message
import hypernova, { load, serialize } from 'hypernova';
import queryString from 'query-string';
import {
  ComponentType,
  ReactNode,
  createElement,
  useEffect,
  useRef,
} from 'react';
import { hydrate } from 'react-dom';
import { renderToStaticMarkup, renderToString } from 'react-dom/server';
import Loadable from 'react-loadable';
import { Provider } from 'react-redux';
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import { Store } from 'redux';
import { ServerStyleSheet } from 'styled-components';

import { ApiUserEnvironment } from 'api/types/ApiUserEnvironment';
import { WithConfig } from 'containers/WithConfig';
import { MainStoreState } from 'modules/main/store';
import { updateSearchLocation } from 'modules/main/store/ducks/mainSearch';
import {
  setABExperimentBucket,
  updateUserEnvironment,
} from 'modules/main/store/ducks/userEnvironment';
import { StaffStoreState } from 'modules/staff/store';
import { bootstrapAnalytics } from 'utils/analytics/setup/bootstrapAnalytics';
import { configureAnalyticsUserData } from 'utils/analytics/setup/configureAnalyticsUserData';
import { registerAbExperiments } from 'utils/analytics/setup/registerAbExperiments';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import configureTrackJs from 'utils/configureTrackJs';
import { initXMLHttpRequestActiveCounter } from 'utils/http';
import { renderAmpPage } from 'utils/renderAmpPage';
import { getInitialSearchLocation } from 'utils/search/getInitialSearchLocation';
import { trackReferrals } from 'utils/trackReferral';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const performanceMark = (...args: any[]) => {
  if (
    window &&
    window.performance &&
    typeof window.performance.mark === 'function'
  ) {
    // @ts-expect-error TS(2556): A spread argument must either have a tuple type or... Remove this comment to see the full error message
    window.performance.mark(...args);
  }
};

function AppContainer({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navType = useNavigationType();
  const hasMounted = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (hasMounted.current) {
      if (navType !== 'REPLACE') {
        trackEvent('JS Page View');
      }
    } else {
      hasMounted.current = true;
    }
  }, [location, navType]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

// Accept a query param to override an AB experiment bucket:
// ?ab_test[button_color]=red
const overrideABExperiments = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  store: any,
  userEnvironment: ApiUserEnvironment,
) => {
  const queryParams = queryString.parse(window.location.search);
  const overrides = Object.keys(queryParams).reduce((acc, param) => {
    const matches = param.match(/^ab_test\[([^\]]+)\]$/);

    if (matches) {
      // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'never'.
      acc.push({ id: matches[1], bucket: queryParams[param] });
    }

    return acc;
  }, []);
  overrides.forEach(({ id, bucket }) => {
    const experiment = userEnvironment.environment.abExperiments.find(
      (e) => e.id === id,
    );

    if (!experiment || experiment.bucket !== bucket) {
      store.dispatch(setABExperimentBucket(id, bucket));
    }
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function initUserPromise(store: any): Promise<ApiUserEnvironment> {
  // Instead of fetching the user here, we're making the request in the
  // HTML template directly. Otherwise, the response from the Link HTTP header is
  // not used and we're making a second request.
  const userPromise = new Promise<ApiUserEnvironment>((resolve) => {
    if (window.Idealist.initUserEnvironment) {
      // We're using setTimeout here because just resolving here introduced
      // some strange issues. Maybe we could get rid of it at some point.
      // @ts-expect-error TS(2345): Argument of type 'ApiUserEnvironment | undefined' ... Remove this comment to see the full error message
      setTimeout(() => resolve(window.Idealist.initUserEnvironment), 1);
    } else {
      // in some cases, the user is in the state from Hypernova
      const { userEnvironment } = store.getState();
      if (
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        userEnvironment &&
        userEnvironment.isUserLoaded &&
        userEnvironment.isEnvironmentLoaded
      ) {
        resolve(userEnvironment);
      }
      const interval = setInterval(() => {
        if (window.Idealist.initUserEnvironment) {
          resolve(window.Idealist.initUserEnvironment);
          clearInterval(interval);
        }
      }, 50);
    }
  });
  return userPromise;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const login = (store: any): Promise<ApiUserEnvironment> => {
  const { searchLocation } = store.getState().mainSearch;
  return initUserPromise(store).then((userEnvironment: ApiUserEnvironment) => {
    store.dispatch(updateUserEnvironment(userEnvironment));
    if (!searchLocation) {
      store.dispatch(
        updateSearchLocation(
          getInitialSearchLocation(userEnvironment.environment.guessedLocation),
          false,
        ),
      );
    }

    return userEnvironment;
  });
};

const renderReact = (
  name: string,
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>,
  createStore: (preloadedState?: MainStoreState | StaffStoreState) => Store,
) =>
  hypernova({
    server() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return Loadable.preloadAll().then(() => (props: any) => {
        const componentWrapper = ({
          url,
          state,
          requestIsAmp,
          config,
        }: {
          url: string;
          state: MainStoreState | StaffStoreState;
          requestIsAmp: boolean;
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          config: Record<string, any>;
        }) => {
          const store = createStore(state);
          const slug = url
            .substring(1, url.length)
            .split('?')[0]
            .replace(/\/$/, '');

          const content =
            state &&
            state.contents &&
            // @ts-expect-error TS(2339): Property 'byUrl' does not exist on type 'never'.
            state.contents.byUrl?.[slug];

          return (
            <Provider store={store}>
              <WithConfig config={config}>
                <StaticRouter location={url}>
                  <Component requestIsAmp={requestIsAmp} content={content} />
                </StaticRouter>
              </WithConfig>
            </Provider>
          );
        };

        const sheet = new ServerStyleSheet();
        const element = createElement(componentWrapper, props);

        if (props.requestIsAmp) {
          const {
            title,
            description,
            canonicalUrl,
            altUrls,
            ampTrackingId,
            ampSchemaMarkup,
          } = props;
          const body = renderToStaticMarkup(
            sheet.collectStyles(element),
          ).replace(/data-style/g, 'style');
          const styleTags = sheet
            .getStyleTags()
            .replace(/<\/?style[^>]*>/g, '')
            .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
          return renderAmpPage({
            title,
            description,
            styles: styleTags,
            body,
            canonicalUrl,
            altUrls,
            ampTrackingId,
            ampSchemaMarkup,
          });
        }

        const html = renderToString(sheet.collectStyles(element));
        const styleTags = sheet
          .getStyleTags()
          .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
        const markup = serialize(name, html, {
          config: props.config,
          state: props.state,
        });
        return `${styleTags}\n${markup}`;
      });
    },

    client() {
      performanceMark('Starting app');
      bootstrapAnalytics();

      // @ts-expect-error TS(2339): Property 'debugXhr' does not exist on type '{ init... Remove this comment to see the full error message
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (window.Idealist && window.Idealist.debugXhr) {
        initXMLHttpRequestActiveCounter();
      }

      const payloads = load(name);

      if (payloads.length > 0) {
        const payload = payloads[0];
        const {
          node,
          data: { state, config },
        } = payload;

        if (node) {
          performanceMark('Before waiting for loadables');
          Loadable.preloadReady().then(() => {
            performanceMark('Done waiting for loadables');
            const store = createStore(state);
            const slug = window.location.pathname
              .substring(1, window.location.pathname.length)
              .split('?')[0]
              .replace(/\/$/, '');
            const content =
              // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
              state &&
              state.contents &&
              state.contents.byUrl &&
              state.contents.byUrl[slug];
            performanceMark('Before setting login state');
            login(store).then((userEnvironment) => {
              performanceMark('Done setting login state');
              configureTrackJs(
                userEnvironment.user ? userEnvironment.user.id : null,
              );
              configureAnalyticsUserData({ userEnvironment });
              trackReferrals();
              store.subscribe(() => {
                registerAbExperiments(
                  store.getState().userEnvironment.environment.abExperiments,
                );
              });
              overrideABExperiments(store, userEnvironment);
              performanceMark('User data first load');
            });
            performanceMark('Starting hydration');
            hydrate(
              <Provider store={store}>
                <WithConfig config={config}>
                  <BrowserRouter>
                    <AppContainer>
                      <Component content={content} />
                    </AppContainer>
                  </BrowserRouter>
                </WithConfig>
              </Provider>,
              node,
              () => performanceMark('App ready'),
            );
          });
        }
      }
    },
  });

// eslint-disable-next-line import/no-default-export
export default renderReact;
